let Instance: string | null = null;

const DuplicateWsSocketError = "duplicate ws-socket";
const NotConnectedError = "not connected";

export const generateUuid = () => {
    const s4 = () => {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1)
    }
    return (s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4())
}

export class Message {

    type: string | undefined
    data: any

    constructor(type: string| undefined, data: object) {
        this.type = type
        this.data = data
    }

    exportJson(): string {
        const obj = {
            ...this.data
        };
        if (this.type) {
            obj["type"] = this.type
        }
        return JSON.stringify(obj)
    }

}

export interface Plugin {

    GetName(): string;

    Init(socket: Socket): void;

    OnConnect(): void;

    OnClose(): void;

    OnMessage(message: Message): void;

    OnMessage(message: Message): void;
}


class Handler {
    fn: (message?: Message) => void;

    constructor(fn: (message?: Message) => void) {
        this.fn = fn;
    }
}

const pollVar = async <T>(valFn: () => T, desired: T) => {
    return new Promise(resolve => {
        const check = (retry: number) => {
            if (valFn() === desired) {
                resolve(null);
            }

            setTimeout(() => check(retry++), 50)
        }
        check(0)
    })
}

type SocketOptions = {
    autoConnect?: boolean
    autoReconnect?: boolean
    plugins?: Plugin[]
}

export class Socket {

    private readonly id: string;
    private readonly handlers: Handler[];
    private readonly onConnect: Handler[];
    private connection: WebSocket | null;
    private options: SocketOptions;
    private connectTries = 0;

    constructor(url: string, options: SocketOptions = {}) {
        this.id = generateUuid();
        if (Instance !== null) {
            throw DuplicateWsSocketError;
        }
        Instance = this.id;
        this.handlers = [];
        this.onConnect = [];
        this.options = options;
        this.connection = null;


        this.options.plugins?.forEach(plugin => plugin.Init(this))
        if (options.autoConnect) {
            this.Connect(url);
        }
    }

    public getPlugin<T>(pluginType: string): T | undefined {
        // @ts-ignore
        return this.options.plugins?.find(plugin => {
            return plugin.GetName() === pluginType;
        })
    }

    public addHandler(handler: (message?: Message)=> void) {
        this.handlers.push(new Handler(handler))
    }

    public addConnectHandler(handler: (message?: Message)=> void) {
        this.onConnect.push(new Handler(handler))
    }

    public Close(code: number, reason: string) {
        return new Promise(async (resolve) => {
            if (this.connection === null) {
                resolve(null);
                return
            }
            this.options.plugins?.forEach(middleware => middleware.OnClose())

            if (this.connection.readyState < 2) {
                this.connection?.close(code, reason);
            }

            this.connection = null;
            resolve(null);
        })
    }

    public GetState(): number {
        return this.connection?.readyState ?? -1;
    }

    public Send(msg: Message) {
        if (this.connection === null || this.connection.readyState !== 1) {
            throw NotConnectedError
        }
        this.connection.send(msg.exportJson())
    }

    public async Connect(url: string) {
        return new Promise(async (resolve, reject) => {
            try {
                if (this.connection !== null) {
                    return
                }

                this.connection = new WebSocket(url)

                this.connection.onclose = async (ev) => {
                    await this.Close(ev.code, ev.reason)
                    if (this.options.autoReconnect) {
                        setTimeout(() => {
                            this.connectTries++;
                            this.Connect(url);
                        }, this.connectTries * 1000);
                    }
                }

                this.connection.onerror = (error): void => {
                    this.Close(4000, "conn_err")
                }

                this.connection.onopen = (): void => {
                    this.connectTries = 0;
                    this.options.plugins?.forEach(middleware => middleware.OnConnect())
                    this.onConnect?.forEach(handler => handler.fn(undefined))
                }

                this.connection.onmessage = (message: MessageEvent<string>): void => {
                    const data = JSON.parse(message.data);
                    const msg = new Message(data.type, data);
                    this.options.plugins?.forEach(plugin => plugin.OnMessage(msg))

                    this.handlers?.forEach(handler => handler.fn(msg))
                }

                // await pollVar(() => this.connection?.readyState ?? 0, 1)
                resolve(null)
            } catch (e) {
                console.error(e);
                reject(e)
            }
        })
    }


}

export default Socket